import { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { ImportUserDataAPI } from "../../services/UserService"; // Adjust path if needed
import { HiDownload } from "react-icons/hi";
import FileUpload from "../Constant/fileupload";

function UserImportModal({ show, onClose, onUploadSuccess }) {
  const [form, setForm] = useState({ file: "" });
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show]);

  const resetForm = () => {
    setErrors([]);
    setForm({ file: "" });
  };

  const handleSubmit = async () => {
    if (!form.file) {
      toast.warning("Please select a file first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", form.file);

    try {
      setUploading(true);
      const res = await ImportUserDataAPI(formData);
      const resData = res.data;

      if (resData.success) {
        toast.success(resData.message || "Upload successful");
        setErrors([]);
        onUploadSuccess();
        onClose();
      } else {
        toast.error(resData.message || "Upload failed");
        setErrors(resData.errors || []);
        if (resData.file) {
          await downloadErrorFile(resData.file);
        }
      }
    } catch (err) {
      console.error("Upload Error:", err);
      const errorData = err.response?.data;

      if (errorData) {
        toast.error(errorData.message || "Upload failed");
        setErrors(errorData.errors || []);

        if (errorData.file) {
          await downloadErrorFile(errorData.file);
        }
      } else {
        toast.error("Unexpected error occurred");
      }
    } finally {
      setUploading(false);
    }
  };

  const downloadErrorFile = async (filePath) => {
    try {
      const fileURL = `${process.env.REACT_APP_BASE_URL}${filePath}`;
      console.log("Downloading file from:", fileURL);

      const response = await fetch(fileURL, {
        method: "GET",
        credentials: "include", // Only if backend needs auth cookies
      });

      if (!response.ok) {
        throw new Error(`File download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const blobURL = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobURL;
      link.download = "User-Import-Errors.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobURL);
    } catch (error) {
      console.error("Download Error:", error);
      toast.error("Failed to download error file.");
    }
  };

  return (
    <Modal show={show} onHide={onClose} scrollable centered>
      <Modal.Header closeButton>
        <Modal.Title>Import Users Excel File</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row p-1">
          <div className="col-12 mb-2 d-flex justify-content-between align-items-center">
            <label className="form-label m-0 text-dark">
              Upload Excel/CSV File
            </label>
            <span
              role="button"
              className="text-primary d-flex align-items-center gap-1"
              onClick={() => {
                const link = document.createElement("a");
                link.href = "/sampleFile/import-user-sample.xlsx";
                link.download = "user-sample-template.xlsx";
                link.click();
              }}
              style={{ fontSize: "0.875rem", cursor: "pointer" }}
            >
              <HiDownload size={16} />
              Sample File
            </span>
          </div>

          <div className="col-12 mt-2 mb-2">
            <FileUpload
              label="Drag & drop File (max size 3MB)"
              accept=".xlsx,.xls,.csv"
              multiple={false}
              value={form.file}
              onChange={(files) => {
                setForm((prev) => ({ ...prev, file: files }));
              }}
            />
          </div>

          <div className="col-12 mt-2">
            <code className="text-light small d-block">
              <strong>Note:</strong> Upload an Excel/CSV file with valid user
              details.
            </code>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="p-2">
            {errors.map((msg, index) => (
              <div
                key={index}
                className="rounded-2 text-bg-danger-soft p-2 mb-1"
                style={{ fontSize: "0.8rem" }}
              >
                {msg}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-end gap-2 w-100">
          <button
            className="btn btn-outline-danger"
            disabled={uploading}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={uploading || !form.file}
          >
            {uploading && (
              <Spinner
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
            )}
            Submit
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default UserImportModal;
